import Table from "components/Table/Table";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { useEffect, useState } from "react";
import type { CellClickArgs } from "react-data-grid";
import { useNavigate, useLocation } from "react-router-dom";
import { setIsLoading } from "reducers/main";
import { useGetClientsQuery } from "services/client-api";
import type { ClientType } from "types/DataTypes";
import { formatCurrency } from "utils/format-utils";
import { useQuery } from "utils/hooks";
import { useDispatch } from "utils/redux";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";

import ClientPageContainer from "./ClientPageContainer";

const CLIENT_COLUMNS = [
  { key: "name", name: "Name", ...commonColumnProps },
  { key: "client_id", name: "Client ID", ...commonColumnProps },
  { key: "email", name: "Email", ...commonColumnProps },
  { key: "billing_email", name: "Billing Email", ...commonColumnProps },
  {
    key: "custom_bill_rate_adjustments",
    name: "Custom Bill Rate Adjustments",
    cellClass: "currency-cell",
    renderCell: (props: { row: ClientType }) => (
      <div>{formatCurrency(props.row.custom_bill_rate_adjustments) || "-"}</div>
    ),
    ...commonColumnProps,
  },
];

const ClientTable = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const query = useQuery();
  const [page, setPage] = useState(1);

  const { data = { results: [], count: 0 }, isLoading } = useGetClientsQuery(
    {
      searchTerm: query.get(GLOBAL_SEARCH_PARAM),
      limit: PAGE_SIZE,
      offset: OFFSET(page),
    },
    { skip: false },
  );

  const handleCellClick = (args: CellClickArgs<ClientType, unknown>) => {
    navigate(`${location.pathname}/${args.row.id}/overview`);
  };

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  return (
    <ClientPageContainer>
      <Table
        className="clients-table"
        columns={CLIENT_COLUMNS}
        rows={data.results}
        rowKeyGetter={(row: ClientType) => row.id}
        onCellClick={handleCellClick}
        fullHeight
        data-testid="clients-table"
        pagination={{
          pageSize: PAGE_SIZE,
          pageIndex: page,
          rowCount: data.count,
          onPageChange: setPage,
          isLoading,
        }}
      />
    </ClientPageContainer>
  );
};

export default ClientTable;
